import React, { useState } from 'react';
import { Box, Typography, CircularProgress, alpha } from '@mui/material';
import { LOJ_COLORS } from '../theme/lojTheme';
import { useSandseaRoster, formatEventDateTime, SandseaLegion, SandseaSquad } from '../hooks/useRoster';
import OrnamentalDivider from './OrnamentalDivider';
import { useTranslation } from '../i18n/LanguageContext';

const ACCENT = LOJ_COLORS.sandLight;

const SQUAD_ACCENT: Record<string, string> = {
  A: LOJ_COLORS.crystalLight,
  B: LOJ_COLORS.goldBright,
  C: LOJ_COLORS.ember,
  D: LOJ_COLORS.sandLight,
  E: LOJ_COLORS.crystalGlow,
  F: LOJ_COLORS.parchment,
};
const accentFor = (letter: string) => SQUAD_ACCENT[letter] ?? LOJ_COLORS.parchmentDark;

// ─── Member row ───────────────────────────────────────────────────────────────

const MemberRow: React.FC<{ name: string; isLeader: boolean }> = ({ name, isLeader }) => {
  const { t } = useTranslation();
  return (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1.5,
      py: 0.8,
      px: 1.5,
      borderBottom: `1px solid ${alpha(LOJ_COLORS.goldDim, 0.1)}`,
      '&:last-child': { borderBottom: 'none' },
      '&:hover': { background: alpha(LOJ_COLORS.goldDim, 0.05) },
    }}
  >
    {isLeader ? (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.6,
          flexShrink: 0,
          px: 1,
          py: 0.2,
          background: alpha(LOJ_COLORS.goldDim, 0.45),
          border: `1px solid ${alpha(LOJ_COLORS.goldBright, 0.45)}`,
          minWidth: 100,
        }}
      >
        <Typography sx={{ fontSize: '0.75rem', lineHeight: 1 }}>⭐</Typography>
        <Typography
          sx={{
            fontFamily: "'Cinzel', serif",
            fontSize: '0.62rem',
            letterSpacing: '0.1em',
            color: LOJ_COLORS.goldGlow,
            fontWeight: 700,
          }}
        >
          {t('LEADER')}
        </Typography>
      </Box>
    ) : (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.6,
          flexShrink: 0,
          px: 1,
          py: 0.2,
          background: alpha(LOJ_COLORS.stone, 0.4),
          border: `1px solid ${alpha(LOJ_COLORS.parchmentDark, 0.3)}`,
          minWidth: 100,
        }}
      >
        <Typography sx={{ fontSize: '0.75rem', lineHeight: 1 }}>⚔️</Typography>
        <Typography
          sx={{
            fontFamily: "'Cinzel', serif",
            fontSize: '0.62rem',
            letterSpacing: '0.1em',
            color: LOJ_COLORS.parchmentDark,
            fontWeight: 700,
          }}
        >
          {t('MEMBER')}
        </Typography>
      </Box>
    )}
    <Typography
      sx={{
        fontFamily: "'Crimson Text', Georgia, serif",
        fontSize: '1.05rem',
        color: LOJ_COLORS.parchmentLight,
        lineHeight: 1.4,
      }}
    >
      {name}
    </Typography>
  </Box>
  );
};

// ─── Squad card ───────────────────────────────────────────────────────────────

const SquadCard: React.FC<{ squad: SandseaSquad }> = ({ squad }) => {
  const { t } = useTranslation();
  const accent = accentFor(squad.squadLetter);
  return (
    <Box
      sx={{
        background: `linear-gradient(135deg, ${alpha(LOJ_COLORS.darkStone, 0.95)}, ${alpha(LOJ_COLORS.stone, 0.85)})`,
        border: `1px solid ${alpha(accent, 0.3)}`,
        borderTop: `2px solid ${accent}`,
        boxShadow: `0 4px 20px ${alpha(LOJ_COLORS.void, 0.7)}`,
        transition: 'box-shadow 0.25s ease',
        '&:hover': {
          boxShadow: `0 6px 28px ${alpha(LOJ_COLORS.void, 0.8)}, 0 0 16px ${alpha(accent, 0.12)}`,
        },
      }}
    >
      {/* Squad header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 2,
          py: 1.2,
          borderBottom: `1px solid ${alpha(accent, 0.2)}`,
          background: alpha(accent, 0.07),
        }}
      >
        <Typography
          sx={{
            fontFamily: "'Cinzel', serif",
            fontWeight: 700,
            fontSize: '0.85rem',
            letterSpacing: '0.12em',
            color: accent,
            textShadow: `0 0 12px ${alpha(accent, 0.4)}`,
          }}
        >
          {t('SQUAD')} {squad.squadLetter}
        </Typography>
        <Box sx={{ ml: 'auto', fontFamily: "'Cinzel', serif", fontSize: '0.65rem', color: alpha(accent, 0.6), letterSpacing: '0.08em' }}>
          {squad.members.length} {t('MEMBERS')}
        </Box>
      </Box>

      {/* Members */}
      <Box>
        {squad.members.map((m, i) => (
          <MemberRow key={i} name={m.name} isLeader={m.isLeader} />
        ))}
      </Box>
    </Box>
  );
};

// ─── Legion panel ─────────────────────────────────────────────────────────────

const LegionPanel: React.FC<{ legion: SandseaLegion; isActive: boolean }> = ({ legion, isActive }) => {
  const { t } = useTranslation();
  const totalMembers = legion.squads.reduce((sum, s) => sum + s.members.length, 0);

  return (
    <Box
      sx={{
        display: isActive ? 'block' : 'none',
        animation: isActive ? 'fadeIn 0.35s ease' : 'none',
        '@keyframes fadeIn': {
          from: { opacity: 0, transform: 'translateY(8px)' },
          to:   { opacity: 1, transform: 'translateY(0)'   },
        },
      }}
    >
      {/* Event time + tactician banner */}
      <Box
        sx={{
          mb: 3,
          p: { xs: 2, md: 2.5 },
          background: `linear-gradient(135deg, ${alpha(LOJ_COLORS.darkStone, 0.95)}, ${alpha(LOJ_COLORS.stone, 0.8)})`,
          border: `1px solid ${alpha(ACCENT, 0.3)}`,
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 3,
        }}
      >
        {/* Date/time */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography sx={{ fontSize: '1.3rem' }}>📅</Typography>
          <Box>
            <Typography
              sx={{ fontFamily: "'Cinzel', serif", fontSize: '0.7rem', letterSpacing: '0.15em', color: LOJ_COLORS.goldMuted, mb: 0.3 }}
            >
              {t('EVENT TIME')}
            </Typography>
            <Typography
              sx={{ fontFamily: "'Cinzel', serif", fontSize: { xs: '0.9rem', md: '1rem' }, fontWeight: 600, color: LOJ_COLORS.goldBright, letterSpacing: '0.05em' }}
            >
              {formatEventDateTime(legion.date, legion.time)}
            </Typography>
          </Box>
        </Box>

        {/* Tactician */}
        {legion.tactician && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              px: 2,
              py: 1,
              background: `linear-gradient(135deg, ${alpha(LOJ_COLORS.crystalDeep, 0.55)}, ${alpha(LOJ_COLORS.darkStone, 0.7)})`,
              border: `1px solid ${alpha(LOJ_COLORS.crystalLight, 0.5)}`,
              borderLeft: `3px solid ${LOJ_COLORS.crystalLight}`,
              boxShadow: `0 0 12px ${alpha(LOJ_COLORS.crystalGlow, 0.2)}`,
            }}
          >
            <Typography sx={{ fontSize: '1.3rem', flexShrink: 0 }}>🔭</Typography>
            <Box>
              <Typography
                sx={{
                  fontFamily: "'Cinzel', serif",
                  fontSize: '0.65rem',
                  letterSpacing: '0.2em',
                  color: LOJ_COLORS.crystalLight,
                  fontWeight: 700,
                  mb: 0.2,
                  textShadow: `0 0 8px ${alpha(LOJ_COLORS.crystalGlow, 0.6)}`,
                }}
              >
                ✦ {t('TACTICIAN')} ✦
              </Typography>
              <Typography
                sx={{
                  fontFamily: "'Cinzel', serif",
                  fontSize: { xs: '0.95rem', md: '1.05rem' },
                  fontWeight: 700,
                  color: LOJ_COLORS.crystalGlow,
                  letterSpacing: '0.06em',
                  textShadow: `0 0 14px ${alpha(LOJ_COLORS.crystalGlow, 0.7)}`,
                }}
              >
                {legion.tactician}
              </Typography>
            </Box>
          </Box>
        )}

        <Box sx={{ ml: 'auto', textAlign: 'right' }}>
          <Typography sx={{ fontFamily: "'Cinzel', serif", fontSize: '0.65rem', letterSpacing: '0.12em', color: LOJ_COLORS.parchmentDark }}>
            {legion.squads.length} {t('SQUADS')} · {totalMembers} {t('MEMBERS')}
          </Typography>
        </Box>
      </Box>

      {/* Squad grid */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(3, 1fr)' },
          gap: 2,
        }}
      >
        {legion.squads.map(squad => (
          <SquadCard key={squad.squadLetter} squad={squad} />
        ))}
      </Box>
    </Box>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

const SandseaRoster: React.FC = () => {
  const { t } = useTranslation();
  const [activeLegion, setActiveLegion] = useState(0);
  const { legions, loading, error, lastUpdated } = useSandseaRoster();

  return (
    <Box sx={{ px: { xs: 2, md: 4 }, mb: 4 }}>
      <OrnamentalDivider label={t('Event Roster')} color={ACCENT} />

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 3 }}>
        <Typography
          variant="h4"
          sx={{ fontSize: '1.1rem', color: ACCENT, display: 'flex', alignItems: 'center', gap: 1 }}
        >
          <span>🏜️</span> {t('Sandsea Conquest — Legion Assignments')}
        </Typography>
        {lastUpdated && (
          <Typography sx={{ fontFamily: "'Cinzel', serif", fontSize: '0.65rem', letterSpacing: '0.1em', color: LOJ_COLORS.goldDim }}>
            {t('LOADED')} {lastUpdated}
          </Typography>
        )}
      </Box>

      {/* Loading */}
      {loading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 4, justifyContent: 'center' }}>
          <CircularProgress size={20} sx={{ color: ACCENT }} />
          <Typography sx={{ fontFamily: "'Cinzel', serif", fontSize: '0.8rem', color: LOJ_COLORS.parchmentDark, letterSpacing: '0.1em' }}>
            {t('LOADING ROSTER...')}
          </Typography>
        </Box>
      )}

      {/* Error */}
      {!loading && error && (
        <Box
          sx={{
            p: 2.5,
            background: alpha(LOJ_COLORS.bloodDark, 0.5),
            border: `1px solid ${alpha(LOJ_COLORS.ember, 0.4)}`,
            borderLeft: `3px solid ${LOJ_COLORS.ember}`,
            display: 'flex',
            gap: 1.5,
          }}
        >
          <Typography sx={{ color: LOJ_COLORS.ember }}>⚠</Typography>
          <Typography sx={{ color: alpha(LOJ_COLORS.ember, 0.9), fontFamily: "'Crimson Text', Georgia, serif", fontSize: '1rem' }}>
            {t(error)}
          </Typography>
        </Box>
      )}

      {/* Content */}
      {!loading && !error && legions.length > 0 && (
        <>
          {/* Legion toggle */}
          <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
            {legions.map((legion, i) => (
              <Box
                key={legion.legion}
                onClick={() => setActiveLegion(i)}
                sx={{
                  flex: '1 1 160px',
                  cursor: 'pointer',
                  p: 2,
                  textAlign: 'center',
                  background: activeLegion === i
                    ? `linear-gradient(135deg, ${alpha(ACCENT, 0.18)}, ${alpha(ACCENT, 0.07)})`
                    : `linear-gradient(135deg, ${alpha(LOJ_COLORS.darkStone, 0.9)}, ${alpha(LOJ_COLORS.stone, 0.7)})`,
                  border: `1px solid ${activeLegion === i ? alpha(ACCENT, 0.7) : alpha(ACCENT, 0.2)}`,
                  borderTop: activeLegion === i ? `3px solid ${ACCENT}` : `1px solid ${alpha(ACCENT, 0.2)}`,
                  boxShadow: activeLegion === i ? `0 0 20px ${alpha(ACCENT, 0.15)}` : 'none',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    borderColor: alpha(ACCENT, activeLegion === i ? 0.7 : 0.4),
                    background: `linear-gradient(135deg, ${alpha(ACCENT, activeLegion === i ? 0.18 : 0.08)}, ${alpha(ACCENT, 0.04)})`,
                  },
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "'Cinzel', serif",
                    fontWeight: 700,
                    fontSize: '0.95rem',
                    letterSpacing: '0.1em',
                    color: activeLegion === i ? ACCENT : LOJ_COLORS.parchment,
                    textShadow: activeLegion === i ? `0 0 12px ${alpha(ACCENT, 0.5)}` : 'none',
                    mb: 0.5,
                  }}
                >
                  {t('Legion')} {legion.legion}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "'Crimson Text', Georgia, serif",
                    fontSize: '0.88rem',
                    color: activeLegion === i ? alpha(ACCENT, 0.8) : LOJ_COLORS.parchmentDark,
                    fontStyle: 'italic',
                  }}
                >
                  {legion.time} ST
                </Typography>
              </Box>
            ))}
          </Box>

          {legions.map((legion, i) => (
            <LegionPanel key={legion.legion} legion={legion} isActive={activeLegion === i} />
          ))}
        </>
      )}

      {/* CSV hint */}
      <Box sx={{ mt: 3, pt: 2, borderTop: `1px solid ${alpha(ACCENT, 0.15)}` }}>
        <Typography sx={{ fontFamily: "'Cinzel', serif", fontSize: '0.65rem', letterSpacing: '0.1em', color: alpha(LOJ_COLORS.parchmentDark, 0.6), textAlign: 'center' }}>
          {t('Roster is loaded from')}{' '}
          <Box component="code" sx={{ color: alpha(ACCENT, 0.7), fontFamily: 'monospace' }}>
            public/sandsea-roster.csv
          </Box>
          {' '}— {t('edit that file each week, then copy it to')}{' '}
          <Box component="code" sx={{ color: alpha(ACCENT, 0.7), fontFamily: 'monospace' }}>
            build/sandsea-roster.csv
          </Box>
        </Typography>
      </Box>
    </Box>
  );
};

export default SandseaRoster;
